<template>
  <div class="san-gao-page" :class="{ 'gift-mode': giftMode }">
    <header class="hero">
      <p class="hero-slogan">三高修复·健康重启</p>
      <p class="hero-description">
        {{ giftMode ? "健康筛查·精准管理" : "专业治疗·长期管理" }}
      </p>
    </header>

    <main class="page-content">
      <section v-if="!giftMode" class="glass-card">
        <SectionTitle number="01" title="业务员信息" />
        <FormField v-model="form.payPersonName" label="业务员姓名" placeholder="请输入姓名" />
        <FormField v-model="form.payPersonNo" label="业务员工号" placeholder="请输入业务员工号" />
        <FormField
          v-model="form.payPersonPhone"
          label="业务员手机号"
          placeholder="请输入手机号"
          type="tel"
          maxlength="11"
        />
      </section>

      <section
        v-for="(customer, customerIndex) in form.itemList"
        :key="customerIndex"
        class="glass-card customer-card"
      >
        <SectionTitle
          :number="giftMode ? '01' : '02'"
          :title="giftMode ? '选择到会礼' : '选择修复项目'"
        />

        <van-loading v-if="projectsLoading" class="project-status" color="#1989d8" vertical>
          正在加载项目...
        </van-loading>
        <div v-else-if="projectsError" class="project-status">
          <p>项目加载失败，请重试</p>
          <van-button size="small" type="info" plain @click="loadRepairProjects">重新加载</van-button>
        </div>
        <div v-else class="project-list">
          <button
            v-for="project in projects"
            :key="project.id"
            type="button"
            class="project-card"
            :class="{ selected: isSelected(customer, project.id) }"
            @click="toggleProject(customerIndex, project)"
          >
            <span class="check-mark">{{ isSelected(customer, project.id) ? "✓" : "" }}</span>
            <span class="project-copy">
              <strong>{{ project.name }}</strong>
              <small>{{ project.description }}</small>
            </span>
            <span class="project-price"><em>￥</em>{{ formatAmount(project.price) }}</span>
          </button>
        </div>

        <template v-if="!giftMode">
          <SectionTitle number="03" title="客户信息" />
          <FormField v-model="customer.personName" label="姓名" placeholder="请输入客户姓名" />
          <FormField
            v-model="customer.personIdCard"
            label="身份证号"
            placeholder="请输入客户身份证号"
            maxlength="18"
          />
          <FormField
            v-model="customer.personPhone"
            label="手机号"
            placeholder="请输入客户手机号"
            type="tel"
            maxlength="11"
          />
        </template>

        <div class="form-field">
          <label>{{ giftMode ? "到会日期" : "开始日期" }}</label>
          <van-cell
            class="date-field"
            :class="{ placeholder: !customer.checkStartTime }"
            :title="customer.checkStartTime || '请选择日期'"
            is-link
            @click="openCalendar(customerIndex)"
          />
        </div>

        <div v-if="customer.productList.length" class="cost-summary">
          <div
            v-for="selected in selectedProjects(customer)"
            :key="selected.id"
            class="cost-row"
          >
            <span>{{ selected.name }}</span>
            <span class="cost-control">
              <b>￥{{ formatAmount(selected.price) }}</b>
              <van-stepper
                v-model="selectedProduct(customer, selected.id).productCount"
                integer
                min="1"
                @change="calculateTotals"
              />
            </span>
          </div>
          <div class="subtotal">
            <span>小计</span>
            <strong>￥{{ formatAmount(customer.sum) }}</strong>
          </div>
        </div>
      </section>

      <section class="glass-card payment-card">
        <SectionTitle :number="giftMode ? '02' : '04'" title="支付方式" />
        <p v-if="!giftMode" class="collection-area">收款地区：{{ collectionAreaName }}</p>
        <div class="payment-options">
          <button
            type="button"
            :class="{ selected: payMode === 1 }"
            @click="payMode = 1"
          >
            <span class="payment-icon"><van-icon name="wechat" /></span>
            <span><strong>本人支付</strong><small>由当前微信立即付款</small></span>
          </button>
          <button
            type="button"
            :class="{ selected: payMode === 2 }"
            @click="payMode = 2"
          >
            <span class="payment-icon"><van-icon name="friends-o" /></span>
            <span><strong>他人代付</strong><small>生成安全链接发给付款人</small></span>
          </button>
        </div>
        <p class="payment-tip">
          {{ giftMode ? "付款人可以是到会人本人或其他人。" : "权益将归属于表单中的客户，付款人可以是客户本人或其他人。" }}
        </p>
      </section>
    </main>

    <footer class="checkout-bar">
      <div class="total-copy">
        <span>合计</span>
        <strong><em>￥</em>{{ formatAmount(total) }}</strong>
      </div>
      <div class="footer-actions">
        <button v-if="!giftMode" type="button" class="save-button" @click="saveDraft">
          暂存
        </button>
        <button type="button" class="pay-button" :disabled="projectsLoading || projectsError || loading" @click="submit">
          {{ payMode === 2 ? "请他人代付" : "立即支付" }}
        </button>
      </div>
    </footer>

    <van-calendar
      v-model="showCalendar"
      :min-date="minDate"
      color="#1387e9"
      @confirm="confirmDate"
    />
    <van-overlay :show="loading">
      <van-loading class="loading" size="40" vertical>支付中...</van-loading>
    </van-overlay>
    <transition name="share-guide-fade">
      <div v-if="showSharePopup" class="wechat-share-guide" aria-hidden="true">
        <svg class="share-guide-arrow" viewBox="0 0 92 70">
          <path d="M8 63 C39 62, 67 43, 78 13" />
          <path d="M65 18 L80 9 L84 25" />
        </svg>
        <div class="share-guide-copy">
          <strong>点击右上角 <span>•••</span></strong>
          <p>选择“发送给朋友”，将代付链接分享给付款人</p>
        </div>
      </div>
    </transition>
    <van-popup v-model="showSharePopup" round position="bottom" class="share-popup">
      <div class="share-symbol"><van-icon name="friends-o" /></div>
      <h3>代付链接已生成</h3>
      <p>点击微信右上角发送给朋友，或复制链接后发给付款人。</p>
      <div class="share-link">{{ shareLink }}</div>
      <button type="button" class="copy-button" @click="copyShareLink">复制代付链接</button>
      <button type="button" class="close-button" @click="showSharePopup = false">稍后分享</button>
    </van-popup>
  </div>
</template>

<script>
import { Toast } from "vant";
import sanGaoShareImage from "@/assets/sanGao/share-card.jpg";
import {
  createSanGaoPayShare,
  getSanGaoRepairProjects,
  onSettleInfoSubmitSanGao,
  onSettleInfoSubmitSanGaoDaoHuiLi,
} from "@/api";
import { weixinInit } from "@/utils/wechatUtil.js";
import { collectionAreas, createCustomer, giftProjects, repairProjects, resolveSanGaoAreaCode } from "./config";

const CACHE_KEYS = {
  repair: "san_gao_repair_cache",
  gift: "san_gao_gift_cache",
};

const SectionTitle = {
  functional: true,
  props: ["number", "title"],
  render(h, context) {
    return h("div", { class: "section-title" }, [
      h("span", context.props.number),
      h("h2", context.props.title),
      h("i"),
    ]);
  },
};

const FormField = {
  functional: true,
  props: ["value", "label", "placeholder", "type", "maxlength"],
  render(h, context) {
    return h("div", { class: "form-field" }, [
      h("label", context.props.label),
      h("van-field", {
        props: {
          value: context.props.value,
          placeholder: context.props.placeholder,
          type: context.props.type || "text",
          maxlength: context.props.maxlength,
        },
        on: {
          input(value) {
            context.listeners.input(value);
          },
        },
      }),
    ]);
  },
};

export default {
  name: "SanGaoInfo",
  components: { FormField, SectionTitle },
  props: {
    giftMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        payPersonName: "",
        payPersonNo: "",
        payPersonPhone: "",
        itemList: [createCustomer()],
      },
      orgId: "",
      areaCode: "",
      companyId: "",
      currentIndex: 0,
      minDate: new Date(),
      showCalendar: false,
      loading: false,
      payMode: 1,
      shareLink: "",
      showSharePopup: false,
      availableRepairProjects: [],
      projectsLoading: false,
      projectsError: false,
    };
  },
  computed: {
    collectionAreaName() {
      const area = collectionAreas.find(item => item.code === this.areaCode);
      return area ? area.name : "无效地区，请重新扫码";
    },
    projects() {
      return this.giftMode ? giftProjects : this.availableRepairProjects;
    },
    cacheKey() {
      return this.giftMode ? CACHE_KEYS.gift : CACHE_KEYS.repair;
    },
    total() {
      return this.form.itemList.reduce((sum, item) => sum + Number(item.sum || 0), 0);
    },
    successPath() {
      return this.giftMode ? "/sanGaoDaoHuiLiInfoSuccess" : "/sanGaoInfoSuccess";
    },
  },
  watch: {
    "$route.query.areaCode"(value) {
      if (!this.giftMode) this.areaCode = resolveSanGaoAreaCode(value);
    },
  },
  created() {
    this.orgId = this.$route.query.orgId || localStorage.getItem("orgId") || "";
    this.companyId = this.$route.query.companyId || localStorage.getItem("companyId") || "";
    // 正式销售只信任本次链接，不能让其他产品或上一笔订单的缓存地区串入。
    this.areaCode = this.giftMode
      ? this.$route.query.areaCode || localStorage.getItem("areaCode") || ""
      : resolveSanGaoAreaCode(this.$route.query.areaCode);
    this.restoreDraft();
    if (!this.giftMode) {
      this.loadRepairProjects();
    }
    this.initWechat();
  },
  methods: {
    async loadRepairProjects() {
      this.projectsLoading = true;
      this.projectsError = false;
      try {
        const response = await getSanGaoRepairProjects();
        if (!response || response.code !== 200 || !Array.isArray(response.data)) {
          throw new Error("项目配置加载失败");
        }
        this.availableRepairProjects = repairProjects.reduce((projects, metadata) => {
          const item = response.data.find(project => Number(project.productId) === metadata.id);
          if (item && Number.isFinite(Number(item.productPrice)) && Number(item.productPrice) > 0) {
            projects.push({ ...metadata, name: item.productName, price: Number(item.productPrice) });
          }
          return projects;
        }, []);
        if (!this.availableRepairProjects.length) throw new Error("暂无在售项目");
        this.reconcileProjects();
      } catch (error) {
        this.projectsError = true;
      } finally {
        this.projectsLoading = false;
      }
    },
    restoreDraft() {
      const cached = localStorage.getItem(this.cacheKey);
      if (!cached) return;
      try {
        const form = JSON.parse(cached);
        if (form && Array.isArray(form.itemList) && form.itemList.length) {
          this.form = {
            ...form,
            itemList: [form.itemList[0]],
          };
          this.form.itemList[0].productList = Array.isArray(this.form.itemList[0].productList)
            ? this.form.itemList[0].productList : [];
          if (this.giftMode) this.reconcileProjects();
          this.calculateTotals();
        }
      } catch (error) {
        localStorage.removeItem(this.cacheKey);
      }
    },
    reconcileProjects() {
      // 历史类型 2 不能自动归入高血压或高尿酸，保留客户资料并让用户重新选择。
      let removed = false;
      this.form.itemList.forEach(customer => {
        const selected = customer.productList;
        customer.productList = selected.filter(item => this.projects.some(project => project.id === item.productId));
        removed = removed || selected.length !== customer.productList.length;
      });
      if (removed) Toast("暂存项目已调整，请重新选择高血压或高尿酸项目");
      this.calculateTotals();
    },
    async initWechat() {
      await weixinInit({
        publicType: this.getPublicType(),
        url: window.location.href.split("#")[0],
        title: "三高修复·健康重启",
        desc: this.giftMode ? "健康筛查·精准管理" : "专业治疗·长期管理",
        imgUrl: this.getShareImageUrl(),
      });
    },
    getPublicType() {
      return this.$route.query.publicType || localStorage.getItem("publicType") || 2;
    },
    getShareImageUrl() {
      return new URL(sanGaoShareImage, window.location.origin).href;
    },
    isSelected(customer, projectId) {
      return customer.productList.some(item => item.productId === projectId);
    },
    selectedProduct(customer, projectId) {
      return customer.productList.find(item => item.productId === projectId);
    },
    selectedProjects(customer) {
      return this.projects.filter(project => this.isSelected(customer, project.id));
    },
    toggleProject(customerIndex, project) {
      const customer = this.form.itemList[customerIndex];
      const selectedIndex = customer.productList.findIndex(item => item.productId === project.id);
      if (selectedIndex >= 0) {
        customer.productList.splice(selectedIndex, 1);
      } else {
        customer.productList.push({
          productId: project.id,
          productCount: 1,
          productPrice: project.price,
        });
      }
      this.calculateTotals();
    },
    calculateTotals() {
      this.form.itemList.forEach(customer => {
        customer.sum = customer.productList.reduce((sum, selected) => {
          const project = this.projects.find(item => item.id === selected.productId);
          if (project) selected.productPrice = project.price;
          return sum + (project ? project.price * Number(selected.productCount || 1) : 0);
        }, 0);
      });
    },
    openCalendar(index) {
      this.currentIndex = index;
      this.showCalendar = true;
    },
    confirmDate(date) {
      const pad = value => (value < 10 ? `0${value}` : String(value));
      this.form.itemList[this.currentIndex].checkStartTime = `${date.getFullYear()}-${pad(
        date.getMonth() + 1
      )}-${pad(date.getDate())}`;
      this.showCalendar = false;
    },
    formatAmount(value) {
      return Number(value || 0).toLocaleString("zh-CN");
    },
    saveDraft() {
      localStorage.setItem(this.cacheKey, JSON.stringify(this.form));
      Toast("暂存成功");
    },
    validate() {
      if (this.projectsLoading) return "项目正在加载，请稍候";
      if (this.projectsError || !this.projects.length) return "请先重新加载项目";
      if (!this.giftMode) {
        if (this.areaCode == null) return "收款地区参数无效，请重新扫描地区收款码";
        if (!this.form.payPersonName.trim()) return "请填写业务员姓名";
        if (!this.form.payPersonNo.trim()) return "请填写业务员工号";
        if (!/^1\d{10}$/.test(this.form.payPersonPhone)) return "请输入正确的业务员手机号";
      }

      for (const customer of this.form.itemList) {
        if (!customer.productList.length) return "请至少选择一个项目";
        if (!this.giftMode) {
          if (!customer.personName.trim()) return "请填写客户姓名";
          if (!customer.personIdCard.trim()) return "请填写客户身份证号";
          if (!/^1\d{10}$/.test(customer.personPhone)) return "请输入正确的客户手机号";
        }
        if (!customer.checkStartTime) return this.giftMode ? "请选择到会日期" : "请选择开始日期";
      }
      return "";
    },
    submit() {
      if (this.loading) return;
      const error = this.validate();
      if (error) {
        Toast(error);
        return;
      }
      if (this.payMode === 2) {
        this.createSharePayment();
      } else {
        this.submitPayment();
      }
    },
    buildPayload() {
      return {
        ...this.form,
        wxCode: localStorage.getItem("code") || "",
        saleOrgId: this.orgId,
        areaCode: this.areaCode,
        productMark: this.giftMode ? 44 : 43,
        payMode: this.payMode,
      };
    },
    async submitPayment() {
      this.loading = true;
      const payload = this.buildPayload();
      const submitApi = this.giftMode
        ? onSettleInfoSubmitSanGaoDaoHuiLi
        : onSettleInfoSubmitSanGao;
      try {
        const response = await submitApi(payload);
        if (response.code === 200) {
          this.weixinPay(response.data);
        } else {
          Toast(response.msg || "提交失败，请重试");
          this.loading = false;
        }
      } catch (error) {
        this.loading = false;
        Toast("网络异常，请稍后重试");
      }
    },
    async createSharePayment() {
      this.loading = true;
      try {
        const response = await createSanGaoPayShare(this.buildPayload());
        if (response.code !== 200 || !response.data || !response.data.shareToken) {
          Toast(response.msg || "生成代付链接失败");
          return;
        }
        localStorage.setItem(this.cacheKey, JSON.stringify(this.form));
        const shareUrl = new URL("/sanGaoPay", window.location.origin);
        shareUrl.searchParams.set("shareToken", response.data.shareToken);
        shareUrl.searchParams.set("publicType", this.getPublicType());
        this.shareLink = shareUrl.href;
        const shareConfig = {
          publicType: this.getPublicType(),
          url: window.location.href.split("#")[0],
          shareUrl: this.shareLink,
          title: "三高修复·健康重启",
          desc: `${this.giftMode ? "三高修复到会礼" : "三高修复"}，待支付 ¥${this.formatAmount(response.data.totalPrice)}`,
          imgUrl: this.getShareImageUrl(),
        };
        let shareReady = await weixinInit(shareConfig);
        if (!shareReady) {
          // iOS 微信偶发首次注册未就绪，再注册一次分享数据，不要求用户刷新页面。
          shareReady = await weixinInit(shareConfig);
        }
        this.showSharePopup = true;
        if (!shareReady) {
          Toast("微信分享初始化失败，请使用复制链接分享");
        }
      } catch (error) {
        Toast("网络异常，请稍后重试");
      } finally {
        this.loading = false;
      }
    },
    async copyShareLink() {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(this.shareLink);
        } else {
          const input = document.createElement("input");
          input.value = this.shareLink;
          document.body.appendChild(input);
          input.select();
          document.execCommand("copy");
          document.body.removeChild(input);
        }
        Toast("代付链接已复制");
      } catch (error) {
        Toast("复制失败，请长按链接复制");
      }
    },
    weixinPay(payParams) {
      if (!payParams) {
        this.loading = false;
        Toast("未获取到支付信息");
        return;
      }
      const invokePay = () => {
        const payNo = payParams.payNo;
        const cacheKey = this.cacheKey;
        const successPath = this.successPath;
        window.WeixinJSBridge.invoke(
          "getBrandWCPayRequest",
          {
            appId: payParams.appId,
            timeStamp: payParams.timeStamp,
            nonceStr: payParams.nonceStr,
            package: payParams.packages,
            signType: payParams.signType,
            paySign: payParams.paySign,
          },
          result => {
            this.loading = false;
            if (result.err_msg === "get_brand_wcpay_request:ok") {
              localStorage.removeItem(cacheKey);
              window.location.href = `${window.location.origin}${successPath}?payNo=${payNo}`;
            } else if (result.err_msg !== "get_brand_wcpay_request:cancel") {
              Toast("支付未完成，请重试");
            }
          }
        );
      };

      if (typeof window.WeixinJSBridge === "undefined") {
        document.addEventListener("WeixinJSBridgeReady", invokePay, false);
      } else {
        invokePay();
      }
    },
  },
};
</script>

<style lang="less" scoped>
* {
  box-sizing: border-box;
}

button {
  font: inherit;
}

.san-gao-page {
  position: relative;
  min-height: 100vh;
  padding-bottom: 108px;
  overflow: hidden;
  color: #163451;
  background: #0874ca;
}

.san-gao-page::before,
.san-gao-page::after {
  position: fixed;
  inset: 0;
  content: "";
  pointer-events: none;
}

.san-gao-page::before {
  z-index: 0;
  background: url("../../assets/sanGao/background.jpg") center -86px / cover no-repeat;
  opacity: 0.5;
}

.san-gao-page::after {
  z-index: 0;
  background: linear-gradient(180deg, rgba(0, 75, 164, 0.1) 0%, rgba(196, 235, 255, 0.58) 38%, rgba(224, 247, 255, 0.9) 58%, #dff6ff 100%);
}

.hero,
.page-content,
.checkout-bar {
  position: relative;
  z-index: 1;
}

.hero {
  height: 270px;
  padding: 76px 24px 0;
  color: #fff;
  text-shadow: 0 3px 16px rgba(0, 48, 113, 0.45);
}

.hero-slogan {
  margin: 0;
  font-size: 30px;
  font-weight: 800;
  line-height: 1.28;
  letter-spacing: 2.5px;
}

.hero-description {
  margin-top: 14px;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 3px;
  opacity: 0.94;
}

.page-content {
  padding: 0 16px;
}

.glass-card {
  margin-bottom: 18px;
  padding: 20px 16px 6px;
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.93);
  box-shadow: 0 10px 30px rgba(12, 91, 147, 0.13);
  backdrop-filter: blur(8px);
}

/deep/ .section-title {
  display: flex;
  align-items: center;
  margin: 2px 0 18px;
}

/deep/ .section-title span {
  margin-right: 9px;
  font-size: 11px;
  font-weight: 700;
  color: #38a1e8;
}

/deep/ .section-title h2 {
  margin: 0;
  font-size: 18px;
  color: #163b5c;
}

/deep/ .section-title i {
  flex: 1;
  height: 1px;
  margin-left: 12px;
  background: linear-gradient(90deg, #b8def5, transparent);
}

/deep/ .form-field {
  margin-bottom: 16px;
}

/deep/ .form-field label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  color: #42637d;
}

/deep/ .form-field .van-cell {
  min-height: 46px;
  padding: 12px 14px;
  border: 1px solid #d8ebf7;
  border-radius: 10px;
  background: #f2f9fd;
}

/deep/ .form-field .van-field__control,
/deep/ .date-field .van-cell__title {
  color: #173c5d;
}

/deep/ .form-field input::placeholder,
/deep/ .date-field.placeholder .van-cell__title {
  color: #9ab7cb;
}

.project-status {
  margin: 16px 0 24px;
  text-align: center;
  color: #66849a;
  font-size: 14px;
}

.collection-area {
  margin: -8px 0 16px;
  color: #66849a;
  font-size: 14px;
}

.pay-button:disabled {
  opacity: 0.6;
}

.project-list {
  display: grid;
  gap: 10px;
  margin-bottom: 24px;
}

.project-card {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 76px;
  padding: 13px 12px;
  overflow: hidden;
  text-align: left;
  border: 1px solid #cce4f5;
  border-radius: 12px;
  color: #244861;
  background: linear-gradient(135deg, #f7fcff, #edf8ff);
}

.project-card.selected {
  border-color: #168de0;
  background: linear-gradient(135deg, #e9f7ff, #f8fdff);
  box-shadow: inset 3px 0 #168de0;
}

.check-mark {
  display: flex;
  flex: 0 0 22px;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-right: 10px;
  border: 1px solid #a8cade;
  border-radius: 50%;
  color: #fff;
  background: #fff;
}

.selected .check-mark {
  border-color: #168de0;
  background: #168de0;
}

.project-copy {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  padding-right: 8px;
}

.project-copy strong {
  font-size: 14px;
  line-height: 1.45;
}

.project-copy small {
  margin-top: 4px;
  font-size: 11px;
  color: #7799af;
}

.project-price {
  flex: 0 0 auto;
  font-size: 18px;
  font-weight: 700;
  color: #e88137;
}

.project-price em,
.total-copy em {
  font-size: 12px;
  font-style: normal;
}

.cost-summary {
  margin: 4px -2px 16px;
  padding: 4px 12px 0;
  border-radius: 12px;
  background: rgba(232, 246, 254, 0.68);
}

.cost-row,
.subtotal {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
  font-size: 12px;
  border-bottom: 1px solid #dceef8;
}

.cost-control {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cost-control b,
.subtotal strong {
  color: #e7772f;
}

.subtotal {
  border-bottom: 0;
  font-size: 14px;
}

.payment-card {
  padding-bottom: 18px;
}

.payment-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.payment-options button {
  display: flex;
  align-items: center;
  min-height: 74px;
  padding: 11px;
  text-align: left;
  border: 1px solid #cfe4f3;
  border-radius: 12px;
  color: #31566e;
  background: #f6fbfe;
}

.payment-options button.selected {
  border-color: #168de0;
  background: #eaf7ff;
  box-shadow: inset 0 0 0 1px rgba(22, 141, 224, 0.18);
}

.payment-options button > span:last-child {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.payment-options strong {
  margin-bottom: 5px;
  font-size: 14px;
}

.payment-options small {
  font-size: 10px;
  line-height: 1.45;
  color: #83a0b4;
}

.payment-icon {
  display: flex;
  flex: 0 0 30px;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  margin-right: 8px;
  border-radius: 50%;
  color: #fff;
  background: linear-gradient(135deg, #27b1e9, #1979d6);
}

.payment-icon .van-icon {
  font-size: 18px;
}

.payment-tip {
  margin: 12px 2px 0;
  font-size: 11px;
  line-height: 1.6;
  color: #7896aa;
}

.share-popup {
  padding: 28px 22px calc(22px + env(safe-area-inset-bottom));
  text-align: center;
  color: #21435d;
}

.wechat-share-guide {
  position: fixed;
  top: calc(8px + env(safe-area-inset-top));
  right: 12px;
  z-index: 2003;
  display: flex;
  width: 250px;
  align-items: flex-end;
  flex-direction: column;
  pointer-events: none;
  color: #fff;
  filter: drop-shadow(0 4px 12px rgba(0, 42, 91, 0.3));
}

.share-guide-arrow {
  width: 82px;
  height: 62px;
  margin-right: 0;
  overflow: visible;
  fill: none;
  stroke: #fff;
  stroke-width: 4;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.share-guide-copy {
  width: 230px;
  margin-top: -3px;
  padding: 12px 14px;
  text-align: left;
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 12px;
  background: rgba(12, 70, 123, 0.9);
  backdrop-filter: blur(8px);
}

.share-guide-copy strong {
  display: block;
  margin-bottom: 5px;
  font-size: 15px;
  font-weight: 700;
}

.share-guide-copy strong span {
  letter-spacing: 2px;
}

.share-guide-copy p {
  margin: 0;
  font-size: 12px;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.9);
}

.share-guide-fade-enter-active,
.share-guide-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.share-guide-fade-enter,
.share-guide-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.share-symbol {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  margin: 0 auto 14px;
  border-radius: 50%;
  color: #fff;
  background: linear-gradient(135deg, #24afea, #176bd1);
}

.share-symbol .van-icon {
  font-size: 26px;
}

.share-popup h3 {
  margin: 0 0 8px;
  font-size: 20px;
}

.share-popup p {
  margin: 0 auto 16px;
  font-size: 13px;
  line-height: 1.65;
  color: #6e8ba0;
}

.share-link {
  padding: 12px;
  overflow-wrap: anywhere;
  text-align: left;
  border-radius: 10px;
  font-size: 11px;
  line-height: 1.5;
  color: #4b718b;
  background: #edf7fc;
}

.copy-button,
.close-button {
  width: 100%;
  height: 42px;
  margin-top: 14px;
  border-radius: 21px;
  font-size: 14px;
  font-weight: 500;
}

.copy-button {
  border: 0;
  color: #fff;
  background: linear-gradient(100deg, #19a2e5, #146bd2);
}

.close-button {
  margin-top: 8px;
  border: 0;
  font-size: 13px;
  font-weight: 400;
  color: #6c899c;
  background: #f1f7fa;
}

.checkout-bar {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 84px;
  padding: 12px 14px 22px;
  border-radius: 18px 18px 0 0;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 -8px 24px rgba(18, 82, 126, 0.12);
}

.total-copy {
  display: flex;
  flex-direction: column;
}

.total-copy > span {
  margin-bottom: 2px;
  font-size: 11px;
  color: #7894a7;
}

.total-copy strong {
  font-size: 21px;
  color: #e7762e;
}

.footer-actions {
  display: flex;
  gap: 8px;
}

.footer-actions button {
  height: 42px;
  padding: 0 18px;
  border-radius: 21px;
  font-size: 14px;
}

.save-button {
  border: 1px solid #65ade0;
  color: #167fc7;
  background: #fff;
}

.pay-button {
  min-width: 112px;
  border: 0;
  color: #fff;
  background: linear-gradient(100deg, #19a2e5, #146bd2);
  box-shadow: 0 6px 15px rgba(17, 112, 199, 0.25);
}

.loading {
  position: absolute;
  top: 42%;
  left: 50%;
  color: #fff;
  transform: translate(-50%, -50%);
}
</style>
